import React, {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import {Tag, Image, ErrorBlock, Tabs, NavBar, ImageViewer, Skeleton} from 'antd-mobile';
import {
    GET_DSP_PACKAGE_DETAIL_BY_TRACKING_NUMBER,
    GetDspPackageDetailByTrackingNumberResponse,
    GetDspPackageDetailByTrackingNumberVariables
} from '../../graphql/queries/getPackageDetail';
import {DspPath} from '../../../../enums/DeliveryNavigatePathEnum';
import {OverallStatus} from '../../../../enums/DeliveryStatusEnum';
import {formatTimestampToSecondsTime} from "../../../../utils/dateTimeUtils";
import {getNavBarContainerStyle} from "../../../../utils/deviceUtils";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {useDspStatusInternationalization} from '../../../../hooks/useDspStatusInternationalization';
import DspPackageTimeline from '../../components/DspPackageTimeline';
import {formatStopNumber} from "../../../../utils/stopNumberUtils";
import OrderNoteItem from '../../../../components/OrderNoteItem/OrderNoteItem';
import {maskPhoneNumber} from "../../../../utils/phoneUtils";
import {InfoRow} from "../../../../components/InfoRow";
import {getOrderNoteContent} from '../../../../utils/orderNoteUtils';

const DspTrackDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const {trackingNumber} = useParams<{ trackingNumber: string }>();
    const {t, i18n} = useTranslation();
    const [activeTab, setActiveTab] = useState('basicInfo');
    const [imageViewerVisible, setImageViewerVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [currentImages, setCurrentImages] = useState<string[]>([]);
    const {getStatusText, getStatusColor} = useDspStatusInternationalization();

    // 查询DSP包裹详情
    const {
        data: packageData,
        loading,
        error
    } = useQuery<GetDspPackageDetailByTrackingNumberResponse, GetDspPackageDetailByTrackingNumberVariables>(
        GET_DSP_PACKAGE_DETAIL_BY_TRACKING_NUMBER,
        {
            variables: {
                trackingNumber: trackingNumber || ""
            },
            skip: !trackingNumber,
            fetchPolicy: 'cache-and-network'
        }
    );

    const packageDetail = packageData?.getDspPackageDetailByTrackingNumber;


    // 处理图片点击事件
    const handleImageClick = (images: string[], index: number) => {
        setCurrentImages(images);
        setCurrentImageIndex(index);
        setImageViewerVisible(true);
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen bg-white">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>{trackingNumber}</NavBar>
                </div>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={5} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={1} animated/>
                <Skeleton style={{width: '100%', height: '30%'}} animated/>
            </div>
        );
    }

    if (error || !packageDetail) {
        return (
            <div className="p-4">
                <ErrorBlock
                    status="empty"
                    title={t('pages.dsp.packageDetail.packageNotFound', '包裹不存在')}
                    description={t('pages.dsp.packageDetail.packageNotFoundDescription', '未找到该运单号对应的包裹信息')}
                />
            </div>
        );
    }

    // 基础信息Tab内容
    const renderBasicInfoTab = () => (
        <div className="bg-gray-50 min-h-full">
            {/* 订单状态 */}
            <div className="bg-white p-1">
                <div className="flex justify-between items-center">
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.parcelStatus')}</h2>
                    <div
                        className={`px-2 py-2 rounded-lg text-white text-sm font-medium min-w-20 max-w-32 text-center break-words ${
                            getStatusColor(packageDetail.orderStatus) === 'success' ? 'bg-green-500' :
                                getStatusColor(packageDetail.orderStatus) === 'primary' ? 'bg-blue-500' :
                                    getStatusColor(packageDetail.orderStatus) === 'danger' ? 'bg-red-500' :
                                        getStatusColor(packageDetail.orderStatus) === 'warning' ? 'bg-orange-500' :
                                            'bg-gray-500'
                        }`}
                    >
                        {getStatusText(packageDetail.orderStatus)}
                    </div>
                </div>
            </div>
            {/* 基础信息 */}
            <div className="bg-white p-1">
                <div className="space-y-1">
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.orderInfo')}</h2>
                    <InfoRow
                        label={t('pages.dsp.packageDetail.trackingNo')}
                        value={packageDetail.trackingNumber}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.stop')}
                        value={packageDetail?.deliveryTaskOrder?.stopNumber != null ? formatStopNumber(packageDetail?.deliveryTaskOrder?.stopNumber,packageDetail?.deliveryTaskOrder?.subStopNumber) : '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.route')}
                        value={packageDetail?.deliveryTaskOrder?.routeName || '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.assignedTime')}
                        value={packageDetail?.deliverAt ? formatTimestampToSecondsTime(packageDetail?.deliverAt) : '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.deliveryAttempt')}
                        value={`${packageDetail?.deliveryAttempts || 0} out of 3`}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.driver')}
                        value={packageDetail?.assignedDriver || '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.taskDate')}
                        value={packageDetail?.taskDate || '-'}
                    />
                    <OrderNoteItem
                        orderNote={packageDetail?.deliveryTaskOrder?.orderNote}
                        currentLanguage={i18n.language}
                    />
                    {/* 根据isDelivered状态显示不同的信息 */}
                    {(packageDetail?.isDelivered || packageDetail?.orderStatus === OverallStatus.PRE_DELIVERED) ? (
                        <>
                            <InfoRow
                                label={t('pages.dsp.packageDetail.deliveredAt')}
                                value={packageDetail?.preDeliveredAt ? formatTimestampToSecondsTime(packageDetail.preDeliveredAt) : (packageDetail?.deliveredAt ? formatTimestampToSecondsTime(packageDetail.deliveredAt) : '-')}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.deliveredBy')}
                                value={packageDetail?.deliveredBy || '-'}
                            />
                        </>
                    ) : packageDetail?.orderStatus === 'DELIVERY_FAILED' ? (
                        <>
                            <InfoRow
                                label={t('pages.dsp.packageDetail.failAt')}
                                value={formatTimestampToSecondsTime(packageDetail?.failAt || '-')}
                            />
                            <InfoRow
                                label={t('pages.dsp.packageDetail.markedBy')}
                                value={packageDetail?.markedBy || '-'}
                            />
                        </>
                    ) : null}
                </div>
            </div>

            {/* 客户信息 */}
            <div className="bg-white p-1">
                <div className="flex items-center mb-3">
                    <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.customerInfo')}</h2>
                </div>

                <div className="space-y-1">
                    <InfoRow
                        label={t('pages.dsp.packageDetail.customerName')}
                        value={packageDetail?.recipientName || '-'}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.phoneNumber')}
                        value={maskPhoneNumber(packageDetail?.recipientPhone)}
                    />
                    <InfoRow
                        label={t('pages.dsp.packageDetail.address')}
                        value={packageDetail?.recipientAddress || '-'}
                    />
                </div>
            </div>

            {/* 照片 - 根据状态显示不同的图片 */}
            {(() => {
                let imagesToShow: string[] = [];

                if (packageDetail?.isDelivered || packageDetail?.orderStatus === OverallStatus.PRE_DELIVERED) {
                    // 已签收或预妥投：显示POD图片
                    imagesToShow = packageDetail?.podImageS3UrlsJson || [];
                } else if (packageDetail?.orderStatus === 'DELIVERY_FAILED') {
                    // 派送失败：显示异常图片
                    imagesToShow = packageDetail?.exceptionImageS3UrlsJson || [];
                }

                // 只有当有图片时才渲染照片区域
                if (!imagesToShow || imagesToShow.length === 0) {
                    return null;
                }

                return (
                    <div className="bg-white p-1">
                        <div className="flex items-center mb-3">
                            <h2 className="text-base font-medium">{t('pages.dsp.packageDetail.photos')}</h2>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            {imagesToShow.map((imageUrl, index) => (
                                <div 
                                    key={index} 
                                    className="aspect-square cursor-pointer"
                                    onClick={() => handleImageClick(imagesToShow, index)}
                                >
                                    <Image
                                        src={imageUrl}
                                        alt={`Package photo ${index + 1}`}
                                        className="w-24 h-24 object-cover rounded-lg"
                                        fit="cover"
                                        width={96}
                                        height={96}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* 司机投递备注 */}
            {packageDetail?.driverDeliveryNote?.note && (
                <div className="bg-white p-1">
                    <h3 className="text-base font-medium mb-1">{t('pages.dsp.packageDetail.driverRemark')}</h3>
                    <p className="text-sm text-gray-700">
                        {getOrderNoteContent(packageDetail.driverDeliveryNote as any, i18n.language)}
                    </p>
                </div>
            )}
        </div>
    );

    // 包裹轨迹Tab内容
    const renderTrackingHistoryTab = () => (
        <div className="bg-gray-50 min-h-full">
            <DspPackageTimeline 
                packageEvents={packageDetail?.packageEvents || []}
                channelName={packageDetail?.channelName}
            />
        </div>
    );

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                    onBack={() => {
                        const params = new URLSearchParams({
                            successPath: DspPath.DELIVERY_DSP_TRACK_DETAIL
                        });
                        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
                    }}
                    backIcon={<ArrowLeftOutlined/>}
                >
                    {packageDetail?.trackingNumber || trackingNumber}
                </NavBar>
            </div>
            {/* Tab切换 */}
            <div className="flex-1 overflow-y-auto">
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    className="px-4"
                >
                    <Tabs.Tab title={t('pages.dsp.packageDetail.basicInfo')} key="basicInfo">
                        {renderBasicInfoTab()}
                    </Tabs.Tab>
                    <Tabs.Tab title={t('pages.dsp.packageDetail.trackingHistory')} key="trackingHistory">
                        {renderTrackingHistoryTab()}
                    </Tabs.Tab>
                </Tabs>
            </div>
            
            {/* 图片查看器 */}
            <ImageViewer.Multi
                images={currentImages}
                visible={imageViewerVisible}
                defaultIndex={currentImageIndex}
                onClose={() => setImageViewerVisible(false)}
                getContainer={null}
            />
        </div>
    );
};


export default DspTrackDetailPage;
